import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useSelector } from 'react-redux';
import { useTheme } from './ThemeContext';
import Spinner from './Spinner';

const AdminCardOrderForm = ({ isOpen, onClose, onSuccess }) => {
  const { currentUser } = useAuth();
  const { theme } = useTheme();
  const cardGroups = useSelector((state) => state.policies.cardGroups);
  const policies = useSelector((state) => state.policies.policies);
  const apiURL = process.env.REACT_APP_API_URL;
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [orderType, setOrderType] = useState('cardgroup'); // 'cardgroup' or 'policy'

  const [formData, setFormData] = useState({
    subdomain: '',
    selectedAccountId: '',
    firstName: '',
    lastName: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zip: '',
    country: 'USA',
    embossedName: '',
    numberOfCards: '1',
    shippingMethod: '1'
  });

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        subdomain: '',
        selectedAccountId: '',
        firstName: '',
        lastName: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        zip: '',
        country: 'USA',
        embossedName: '',
        numberOfCards: '1',
        shippingMethod: '1'
      });
      setOrderType('cardgroup');
      setError('');
      setSuccess('');
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleAccountTypeChange = (e) => {
    setOrderType(e.target.value);
    setFormData({ ...formData, selectedAccountId: '' });
  };

  // Get available subdomains for admin orders
  const availableSubdomains = [
    { value: 'aqha', label: 'AQHA' },
    { value: 'integrity', label: 'Integrity' },
    { value: 'fln', label: 'FLN' },
    { value: 'gofuel', label: 'GoFuel' },
    { value: 'ecapital', label: 'eCapital' },
    { value: 'aznational', label: 'Arizona National' }
  ];

  const shippingMethods = [
    { value: 1, label: 'Standard Shipping' },
    { value: 2, label: 'Ground Shipping' },
    { value: 3, label: 'Overnight Shipping' },
    { value: 4, label: 'Rush Overnight Shipping' },
    { value: 77, label: 'UPS Ground' },
    { value: 72, label: 'UPS Next Day Air' },
    { value: 73, label: 'UPS Next Day Air Saver' },
    { value: 75, label: 'UPS 2nd Day Air' },
    { value: 81, label: 'FedEx Standard Overnight' },
    { value: 85, label: 'FedEx Ground' },
  ];

  const getSelectedAccount = () => {
    if (!formData.selectedAccountId) return null;

    if (orderType === 'cardgroup') {
      return cardGroups?.find(cg => String(cg.cardGroupID) === String(formData.selectedAccountId));
    } else {
      return policies?.find(p => String(p.PolicyNumber) === String(formData.selectedAccountId));
    }
  };

  // EFS API validation functions (matching backend sanitization)
  const efsValidation = {
    validateName: (name, fieldName) => {
      if (!name || !/^[a-zA-Z\s]+$/.test(name)) {
        return `${fieldName} should only contain letters and spaces (EFS API requirement).`;
      }
      return null;
    },

    validateCity: (city) => {
      if (!city || !/^[a-zA-Z\s]+$/.test(city)) {
        return 'City should only contain letters and spaces (EFS API requirement).';
      }
      return null;
    },

    validateAddress: (address, fieldName) => {
      // Check for problematic characters that EFS rejects
      const problematicChars = /[#\-_@$%^&*()+=\[\]{};:"'<>?\/\\|`~]/;
      if (address && problematicChars.test(address)) {
        return `${fieldName} contains special characters that may cause issues with card processing. Consider using: ${address.replace(problematicChars, ' ')}`;
      }
      return null;
    },

    validateEmbossedName: (name) => {
      if (name && !/^[A-Z0-9\s]+$/.test(name.toUpperCase())) {
        return 'Embossed name should only contain letters, numbers, and spaces (EFS API requirement).';
      }
      if (name && name.length > 25) {
        return 'Embossed name must be 25 characters or less.';
      }
      return null;
    }
  };

  const validateForm = () => {
    const zipRegex = /^(?:\d{5}|[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d)$/;

    // Basic required field validation
    if (!formData.subdomain) {
      setError('Please select a subdomain.');
      return false;
    }
    if (!formData.selectedAccountId) {
      setError('Please select an account.');
      return false;
    }
    if (formData.addressLine1.trim() === '') {
      setError('Address Line 1 is required.');
      return false;
    }
    if (formData.city.trim() === '') {
      setError('City is required.');
      return false;
    }
    if (formData.state.trim() === '') {
      setError('State is required.');
      return false;
    }
    if (formData.zip.trim() === '' || !zipRegex.test(formData.zip)) {
      setError('Please enter a valid Zip/Postal Code.');
      return false;
    }
    if (parseInt(formData.numberOfCards) < 1 || parseInt(formData.numberOfCards) > 100) {
      setError('Number of cards must be between 1 and 100.');
      return false;
    }

    // EFS API specific validation
    const nameError = efsValidation.validateName(formData.firstName, 'First Name');
    if (nameError) {
      setError(nameError);
      return false;
    }

    const lastNameError = efsValidation.validateName(formData.lastName, 'Last Name');
    if (lastNameError) {
      setError(lastNameError);
      return false;
    }

    const cityError = efsValidation.validateCity(formData.city);
    if (cityError) {
      setError(cityError);
      return false;
    }

    const address1Error = efsValidation.validateAddress(formData.addressLine1, 'Address Line 1');
    if (address1Error) {
      setError(address1Error);
      return false;
    }

    const address2Error = efsValidation.validateAddress(formData.addressLine2, 'Address Line 2');
    if (address2Error) {
      setError(address2Error);
      return false;
    }

    const embossedError = efsValidation.validateEmbossedName(formData.embossedName);
    if (embossedError) {
      setError(embossedError);
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    if (!validateForm()) return;

    const selectedAccount = getSelectedAccount();
    if (!selectedAccount) {
      setError('Selected account not found.');
      return;
    }

    setLoading(true);
    
    try {
      let payload;

      if (orderType === 'cardgroup') {
        // CardGroup-level order - match orderCardsAdmin expected structure
        payload = {
          subDomain: formData.subdomain,
          userId: currentUser.userId,
          firstName: formData.firstName,
          lastName: formData.lastName,
          addressLine1: formData.addressLine1,
          addressLine2: formData.addressLine2,
          city: formData.city,
          state: formData.state,
          zip: formData.zip,
          country: formData.country,
          embossedName: formData.embossedName || selectedAccount.groupName,
          numberOfCards: parseInt(formData.numberOfCards),
          policyNumber: selectedAccount.PolicyNumber,
          cardGroupID: parseInt(selectedAccount.cardGroupID),
          cardStyle: 50, // Default card style - will be overridden by orderMapping
          shippingMethod: parseInt(formData.shippingMethod)
        };
      } else {
        // Policy-level order - no specific card group
        payload = {
          subDomain: formData.subdomain,
          userId: currentUser.userId,
          firstName: formData.firstName,
          lastName: formData.lastName,
          addressLine1: formData.addressLine1,
          addressLine2: formData.addressLine2,
          city: formData.city,
          state: formData.state,
          zip: formData.zip,
          country: formData.country,
          embossedName: formData.embossedName || (selectedAccount.CompanyName || selectedAccount.Description),
          numberOfCards: parseInt(formData.numberOfCards),
          policyNumber: selectedAccount.PolicyNumber,
          // No cardGroupID for policy-level orders
          cardStyle: 50, // Default card style - will be overridden by orderMapping
          shippingMethod: parseInt(formData.shippingMethod),
          CompanyName: selectedAccount.CompanyName || selectedAccount.Description
        };
      }

      const response = await fetch(`${apiURL}/orderCardsAdmin?`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      // Debug logging
      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);
      console.log('Response headers:', response.headers);

      if (response.ok) {
        const result = await response.json();
        console.log('Success response:', result);
        setSuccess(`Card order submitted successfully! ${result.cardOrderID ? `Order ID: ${result.cardOrderID}` : ''}`);
        setTimeout(() => {
          onSuccess && onSuccess();
          onClose();
        }, 2000);
      } else {
        const errorText = await response.text();
        console.log('Error response status:', response.status);
        console.log('Error response text:', errorText);
        setError(`Server Error: ${errorText}`);
      }
    } catch (error) {
      console.log('Catch block error:', error);
      console.log('Error message:', error.message);
      setError(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  if (loading) {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Fixed Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Admin Card Order</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <span className="sr-only">Close</span>
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto p-6">

          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}
          
          {success && (
            <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
              {success}
            </div>
          )}

          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 text-blue-800 rounded text-sm">
            <p className="font-medium">Card Processing Requirements:</p>
            <ul className="mt-1 list-disc list-inside space-y-1">
              <li>Names and city should only contain letters and spaces</li>
              <li>Avoid special characters like #, -, _, @ in addresses</li>
              <li>Embossed name: letters, numbers, spaces only (25 char max)</li>
            </ul>
            <p className="mt-2 text-xs">
              Note: The system will automatically clean up invalid characters before processing.
            </p>
          </div>

          <form id="admin-card-order-form" onSubmit={handleSubmit} className="space-y-4">
            {/* Subdomain Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Subdomain (Client)
              </label>
              <select
                name="subdomain"
                value={formData.subdomain}
                onChange={handleChange}
                required
                className="mt-1 p-2 block w-full border border-gray-300 rounded-md"
              >
                <option value="">-- Select Client Subdomain --</option>
                {availableSubdomains.map(sub => (
                  <option key={sub.value} value={sub.value}>
                    {sub.label} ({sub.value})
                  </option>
                ))}
              </select>
            </div>

            {/* Account Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Order Type
              </label>
              <div className="flex space-x-4">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    value="cardgroup"
                    checked={orderType === 'cardgroup'}
                    onChange={handleAccountTypeChange}
                    className="form-radio"
                  />
                  <span className="ml-2">Card Group (Specific Account)</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    value="policy"
                    checked={orderType === 'policy'}
                    onChange={handleAccountTypeChange}
                    className="form-radio"
                  />
                  <span className="ml-2">Policy (No Specific Account)</span>
                </label>
              </div>
            </div>

            {/* Account Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Select {orderType === 'cardgroup' ? 'Card Group' : 'Policy'}
              </label>
              <select
                name="selectedAccountId"
                value={formData.selectedAccountId}
                onChange={handleChange}
                required
                className="mt-1 p-2 block w-full border border-gray-300 rounded-md"
              >
                <option value="">-- Select {orderType === 'cardgroup' ? 'Card Group' : 'Policy'} --</option>
                {orderType === 'cardgroup' 
                  ? cardGroups?.map(cg => (
                      <option key={cg.cardGroupID} value={cg.cardGroupID}>
                        {cg.groupName} ({cg.AccountNumber}) - Policy {cg.PolicyNumber}
                      </option>
                    ))
                  : policies?.map(policy => (
                      <option key={policy.PolicyNumber} value={policy.PolicyNumber}>
                        {policy.CompanyName || policy.Description} (Policy {policy.PolicyNumber})
                      </option>
                    ))
                }
              </select>
            </div>

            {/* Shipping Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">First Name</label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  className="mt-1 p-2 block w-full border border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Last Name</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  className="mt-1 p-2 block w-full border border-gray-300 rounded-md"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Address Line 1</label>
              <input
                type="text"
                name="addressLine1"
                value={formData.addressLine1}
                onChange={handleChange}
                required
                className="mt-1 p-2 block w-full border border-gray-300 rounded-md"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Address Line 2 (Optional)</label>
              <input
                type="text"
                name="addressLine2"
                value={formData.addressLine2}
                onChange={handleChange}
                className="mt-1 p-2 block w-full border border-gray-300 rounded-md"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">City</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  required
                  className="mt-1 p-2 block w-full border border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">State</label>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  required
                  className="mt-1 p-2 block w-full border border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">ZIP Code</label>
                <input
                  type="text"
                  name="zip"
                  value={formData.zip}
                  onChange={handleChange}
                  required
                  className="mt-1 p-2 block w-full border border-gray-300 rounded-md"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Country</label>
                <select
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  required
                  className="mt-1 p-2 block w-full border border-gray-300 rounded-md"
                >
                  <option value="USA">United States</option>
                  <option value="CAN">Canada</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Number of Cards</label>
                <input
                  type="number"
                  name="numberOfCards"
                  value={formData.numberOfCards}
                  onChange={handleChange}
                  min="1"
                  max="100"
                  required
                  className="mt-1 p-2 block w-full border border-gray-300 rounded-md"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Shipping Method</label>
              <select
                name="shippingMethod"
                value={formData.shippingMethod}
                onChange={handleChange}
                required
                className="mt-1 p-2 block w-full border border-gray-300 rounded-md"
              >
                {shippingMethods.map(method => (
                  <option key={method.value} value={method.value}>
                    {method.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Embossed Name (Optional)
              </label>
              <input
                type="text"
                name="embossedName"
                value={formData.embossedName}
                onChange={handleChange}
                maxLength="25"
                placeholder="Leave blank to use account name"
                className="mt-1 p-2 block w-full border border-gray-300 rounded-md"
              />
              <p className="text-sm text-gray-500 mt-1">
                Maximum 25 characters. Leave blank to use account name.
              </p>
            </div>
          </form>
        </div>

        {/* Fixed Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="admin-card-order-form"
            disabled={loading}
            style={{ backgroundColor: theme.primary }}
            className="px-4 py-2 text-white rounded-md text-sm font-medium hover:opacity-90 disabled:opacity-50 transition-opacity"
          >
            {loading ? 'Submitting...' : 'Submit Order'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminCardOrderForm;